import { localDate } from "./scoreboard.ts";

/**
 * Trips, zones, and the arithmetic of pausing.
 *
 * A task belongs to a zone: `home` work only accrues time while the family is
 * home, `travel` work only accrues while away, and `always` work (personal
 * routines — teeth are brushed in a tent too) never pauses. "Pausing" is not a
 * flag on the task or a frozen copy of its status: it is extra days added to
 * the due date, so the one source of truth in `schedule.ts` keeps deciding
 * everything and a fortnight away simply pushes every home job's date out a
 * fortnight. Lateness earned before the trip is kept; the trip adds none.
 */

export const ZONES = ["home", "travel", "always"] as const;
export type Zone = (typeof ZONES)[number];

export const ZONE_LABEL: Record<Zone, string> = {
  home: "At home",
  travel: "Travelling",
  always: "Anywhere",
};

export function isZone(value: unknown): value is Zone {
  return typeof value === "string" && (ZONES as readonly string[]).includes(value);
}

/** A trip's dates, local to the household, both ends inclusive. */
export interface TripSpan {
  startsOn: string;
  endsOn: string;
}

/** Whether the household is away on a given local calendar date. */
export function isAwayOn(day: string, trips: readonly TripSpan[]): boolean {
  // ISO dates compare correctly as strings, so no Date objects are needed.
  return trips.some((trip) => trip.startsOn <= day && day <= trip.endsOn);
}

const MS_PER_DAY = 86_400_000;

/** A local date as a whole-day number, so ranges become plain arithmetic. */
function dayNumber(day: string): number {
  return Date.parse(`${day}T00:00:00Z`) / MS_PER_DAY;
}

/**
 * Overlapping trips merged into disjoint day ranges.
 *
 * Two trips that share a week must not count that week twice — a task would
 * come back from holiday owing negative days.
 */
function mergedRanges(trips: readonly TripSpan[]): Array<[number, number]> {
  const ranges = trips
    .map((trip): [number, number] => [dayNumber(trip.startsOn), dayNumber(trip.endsOn)])
    .filter(([start, end]) => end >= start)
    .sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1] + 1) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

/**
 * Away days strictly after `fromDay`, up to and including `toDay`.
 *
 * Half-open on the left to match `daysBetween`'s floor semantics: the day a
 * job was done is its own day whatever happened on it, and only the days that
 * *passed* since can be paused.
 */
export function awayDaysBetween(fromDay: string, toDay: string, trips: readonly TripSpan[]): number {
  const from = dayNumber(fromDay);
  const to = dayNumber(toDay);
  if (to <= from) return 0;

  let days = 0;
  for (const [start, end] of mergedRanges(trips)) {
    const lo = Math.max(start, from + 1);
    const hi = Math.min(end, to);
    if (hi >= lo) days += hi - lo + 1;
  }
  return days;
}

/**
 * How many of the days since `fromDay` this zone's clock was stopped for.
 * The answer feeds straight into `evaluate` as extra days on the due date.
 */
export function pausedDaysFor(zone: Zone, fromDay: string, toDay: string, trips: readonly TripSpan[]): number {
  if (zone === "always") return 0;
  const away = awayDaysBetween(fromDay, toDay, trips);
  if (zone === "home") return away;
  return Math.max(0, dayNumber(toDay) - dayNumber(fromDay)) - away;
}

/** Whether work in this zone is live right now, given whether we are away. */
export function zoneActive(zone: Zone, away: boolean): boolean {
  return zone === "always" || (zone === "travel") === away;
}

/** Convenience for callers holding instants rather than local dates. */
export function pausedDaysAt(
  zone: Zone,
  lastDoneAt: Date | null,
  now: Date,
  trips: readonly TripSpan[],
  timeZone: string,
): number {
  if (lastDoneAt === null) return 0;
  return pausedDaysFor(zone, localDate(lastDoneAt, timeZone), localDate(now, timeZone), trips);
}
